import { createElement, forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/utils";

const textVariants = {
  "body-sm": "text-body-sm",
  "body-md": "text-body-md",
  "body-lg": "text-body-lg",
  "label-sm": "text-label-sm",
  "label-md": "text-label-md",
  "label-lg": "text-label-lg",
  "heading-sm": "text-heading-sm",
  "heading-md": "text-heading-md",
  "heading-lg": "text-heading-lg",
  "heading-xl": "text-heading-xl",
} as const;

const textTones = {
  primary: "text-text-primary",
  secondary: "text-text-secondary",
  muted: "text-text-muted",
  foreground: "text-foreground",
  "muted-foreground": "text-muted-foreground",
} as const;

const textSizes = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
} as const;

export type TextProps = HTMLAttributes<HTMLElement> & {
  as?: "p" | "span";
  tone?: keyof typeof textTones;
} & (
    | { variant?: keyof typeof textVariants; size?: never }
    | { variant?: never; size: keyof typeof textSizes }
  );

/**
 * Typography only: no wrapper, default color, spacing, or data attributes.
 * Keep layout utilities at the call site. Tones preserve the distinct existing
 * color tokens; muted and muted-foreground are deliberately not interchangeable.
 * Omit variant and size to inherit typography. Size preserves the utility scale's
 * line height and inherited weight; variant selects a complete semantic style.
 */
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { as: Component = "p", variant, size, tone, className, ...props },
  ref,
) {
  return createElement(Component, {
    ...props,
    ref,
    className:
      variant || size || tone
        ? cn(
            variant && textVariants[variant],
            size && textSizes[size],
            tone && textTones[tone],
            className,
          )
        : className,
  });
});
